import clsx from 'clsx';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ButtonVariant, Icon, TextButton } from '../../../components';
import { AppRoutes, PUBLISH_PARAM } from '../../../constants';
import { PracticeProspect, ProspectStatus } from '../../../types';
import { isProspectLocked } from '../../../utils';

interface SimulationButtonCellProps {
  canPublishSim: boolean;
  prospect: PracticeProspect;
  runCall: () => void;
}

const SimulationButtonCell = ({ canPublishSim, prospect, runCall }: SimulationButtonCellProps) => {
  const navigate = useNavigate();

  const { status, isDraft, personaId } = prospect;

  const isArchived = status === ProspectStatus.ARCHIVED;
  const isLocked = isProspectLocked(prospect);

  const getStartIcon = () => {
    if (isArchived || isDraft) return;
    if (isLocked) return Icon.LOCK;
    return Icon.PHONE;
  };

  const getButtonText = () => {
    if (isArchived) return 'Archived';
    if (isLocked) return 'Locked';
    if (isDraft) return 'Publish';
    return 'Call';
  };

  const handleClick = useCallback(() => {
    if (isDraft) {
      // Set the publish flag and navigate to the prospect page
      const searchParams = new URLSearchParams();
      searchParams.set(PUBLISH_PARAM, 'true');
      navigate({ pathname: `${AppRoutes.PROSPECT}/${personaId}`, search: searchParams.toString() });
    } else {
      runCall();
    }
  }, [isDraft, runCall, navigate, personaId]);

  const isPublishDisabled = isDraft && !canPublishSim;
  const isCallDisabled = !isDraft && (isArchived || isLocked);

  return (
    <TextButton
      startIcon={getStartIcon()}
      className={clsx('!w-[95px]', isCallDisabled && 'pointer-events-none')}
      text={getButtonText()}
      variant={ButtonVariant.SOFT}
      onClick={handleClick}
      disabled={isPublishDisabled}
      tooltip={isPublishDisabled ? 'You do not have access to publish.' : undefined}
    />
  );
};

export default SimulationButtonCell;
